// Parsing for Google Analytics 4 first-party cookies.
//
// We never call Google directly; we just read GA's own cookies so the backend
// can attach the correct client_id / session_id to its Measurement Protocol
// events. There are two cookies we care about:
//
//   _ga              -> the GA4 client id        (format unchanged by Google)
//   _ga_<container>  -> the GA4 session, per container/measurement id
//
// Google ships the session cookie in (at least) two on-the-wire formats and
// can roll a site from one to the other silently:
//
//   GS1 (legacy):  dot-delimited, fixed positions
//                  GS1.1.<sessionId>.<sessionNumber>.<engaged>.<lastHit>.0.0.0
//
//   GS2 (current): "$"-delimited, self-describing labelled fields whose
//                  ORDER IS NOT GUARANTEED and where optional fields may be
//                  absent entirely:
//                  GS2.1.s<sessionId>$o<sessionNumber>$g<engaged>$t<lastHit>$...
//                  ( s = session id, o = session number/count )
//
// Because GS2 fields are labelled and unordered we parse by label, never by
// position, and we treat any future GS<n>(n>=2) as labelled too. This is the
// fix for the "session cookie parser only understands the old format" defect.

import { reportError } from '../error-reporting';

export type ParsedGaClient = {
    clientId: string;
    clientNumber: string;
};

export type ParsedGaSession = {
    sessionName: string;
    sessionId: string;
    sessionNumber: string;
};

const CLIENT_COOKIE_NAME = '_ga';
const SESSION_COOKIE_PREFIX = '_ga_';

// GA session ids (a unix timestamp) and session numbers (a counter) are always
// strings of digits. Validating this keeps us from emitting garbage if we ever
// read a malformed or unrelated cookie.
const isNumericString = (value: string): boolean => /^\d+$/.test(value);

// Something upstream of GA on some sites rewrites `_ga` by prepending a second
// `GA<n>.<n>.` to an intact value (`GA1.1.GA1.1.<id>.<ts>`). Only the outer
// prefixes are stripped; the inner value parses as usual.
const DUPLICATED_CLIENT_PREFIX = /^(?:GA\d+\.\d+\.)+(?=GA\d+\.\d+\.)/;

// A UUID where the numeric id belongs is not a GA-issued client id, and there
// is no <id>.<timestamp> pair to recover from it. Pinned to the GA1.1 prefix
// seen in the wild, so a future version that adopts UUIDs is still reported.
const UUID_CLIENT_VALUE = /^GA1\.1\.[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

export type RawCookie = { name: string; value: string };

/**
 * Split a `document.cookie` string into name/value pairs. GA cookie values are
 * not URL-encoded, so we only split on the first `=` and trim surrounding
 * whitespace.
 */
export const readCookies = (cookieString: string): RawCookie[] => {
    if (!cookieString) {
        return [];
    }

    const cookies: RawCookie[] = [];

    for (const pair of cookieString.split(';')) {
        const separator = pair.indexOf('=');

        if (separator === -1) {
            continue;
        }

        const name = pair.slice(0, separator).trim();
        const value = pair.slice(separator + 1).trim();

        if (name) {
            cookies.push({ name, value });
        }
    }

    return cookies;
};

/**
 * Parse a `_ga` cookie value into its client id parts.
 *
 * The value looks like `GA<version>.<scopeDepth>.<clientId>.<clientNumber>`
 * e.g. `GA1.2.1234567890.1612345678`. The canonical GA client id is
 * `<clientId>.<clientNumber>`; we keep them split for backwards compatibility
 * with the API (which re-joins them).
 */
export const parseGaClientValue = (value: string): ParsedGaClient | null => {
    const segments = value.replace(DUPLICATED_CLIENT_PREFIX, '').split('.');

    // Need at least version.scopeDepth.clientId.clientNumber
    if (segments.length < 4 || !/^GA\d+$/.test(segments[0])) {
        return null;
    }

    // The GA4 client id is exactly the <clientId>.<firstVisitTimestamp> pair —
    // the value gtag('get', id, 'client_id') returns. We read those two known
    // positions rather than "everything after the prefix", so an extra trailing
    // field (should Google ever add one) is ignored instead of corrupting the
    // id. We keep them split for the API, which re-joins them.
    const clientId = segments[2];
    const clientNumber = segments[3];

    if (!isNumericString(clientId) || !isNumericString(clientNumber)) {
        return null;
    }

    return { clientId, clientNumber };
};

/**
 * Parse a `_ga_<container>` session cookie value, supporting both the legacy
 * dot-positional GS1 format and the current "$"-delimited labelled GS2 format
 * (and any future GS<n>, n>=2, which we assume is labelled).
 */
export const parseGaSessionValue = (value: string): Pick<ParsedGaSession, 'sessionId' | 'sessionNumber'> | null => {
    const versionMatch = value.match(/^GS(\d+)\./);

    if (!versionMatch) {
        return null;
    }

    const version = parseInt(versionMatch[1], 10);
    const isLabelled = version >= 2 || value.includes('$');

    const parsed = isLabelled
        ? parseLabelledSession(value)
        : parsePositionalSession(value);

    if (!parsed) {
        return null;
    }

    if (!isNumericString(parsed.sessionId) || !isNumericString(parsed.sessionNumber)) {
        return null;
    }

    return parsed;
};

/**
 * GS2+: after the `GS<n>.<x>.` prefix the payload is `$`-delimited, labelled,
 * and unordered, e.g. `s1746825440$o14$g0$t1746825440$j60$l0$h0`.
 *   s = session id, o = session number/count.
 */
const parseLabelledSession = (value: string): { sessionId: string; sessionNumber: string } | null => {
    const dotParts = value.split('.');

    if (dotParts.length < 3) {
        return null;
    }

    // Rejoin defensively; the labelled payload itself contains no dots.
    const payload = dotParts.slice(2).join('.');

    const fields: Record<string, string> = {};

    for (const field of payload.split('$')) {
        if (field.length < 2) {
            continue;
        }

        const key = field[0];

        // First occurrence wins; ignore duplicates.
        if (!(key in fields)) {
            fields[key] = field.slice(1);
        }
    }

    if (!('s' in fields) || !('o' in fields)) {
        return null;
    }

    return { sessionId: fields['s'], sessionNumber: fields['o'] };
};

/**
 * GS1: `GS1.<x>.<sessionId>.<sessionNumber>.<...>` — fixed positions.
 */
const parsePositionalSession = (value: string): { sessionId: string; sessionNumber: string } | null => {
    const parts = value.split('.');

    if (parts.length < 4) {
        return null;
    }

    return { sessionId: parts[2], sessionNumber: parts[3] };
};

/**
 * Find and parse the `_ga` client cookie from an already-parsed cookie list
 * (see `readCookies`).
 */
export const parseGaClientCookie = (cookies: readonly RawCookie[]): ParsedGaClient | null => {
    const cookie = cookies.find((c) => c.name === CLIENT_COOKIE_NAME);

    return cookie ? parseGaClientValue(cookie.value) : null;
};

/**
 * Find and parse every `_ga_<container>` session cookie from an already-parsed
 * cookie list. A page can carry more than one (multiple GA4 properties), so we
 * return all of them.
 */
export const parseGaSessionCookies = (cookies: readonly RawCookie[]): ParsedGaSession[] => {
    const sessions: ParsedGaSession[] = [];

    for (const { name, value } of cookies) {
        if (name === CLIENT_COOKIE_NAME || !name.startsWith(SESSION_COOKIE_PREFIX)) {
            continue;
        }

        const sessionName = name.slice(SESSION_COOKIE_PREFIX.length);

        if (!sessionName) {
            continue;
        }

        const parsed = parseGaSessionValue(value);

        if (parsed) {
            sessions.push({ sessionName, ...parsed });
        }
    }

    return sessions;
};

/**
 * Find cookies that clearly intend to be GA cookies (`_ga`/`_ga_<container>`
 * carrying a `GA<n>`/`GS<n>` prefix) but that we failed to parse. This is the
 * "Google changed the cookie format again" signal — it should be empty in
 * normal operation, and anything it returns is worth reporting.
 */
export const findUnparsableGaCookies = (cookies: readonly RawCookie[]): RawCookie[] => {
    const unparsable: RawCookie[] = [];

    for (const { name, value } of cookies) {
        const isClient = name === CLIENT_COOKIE_NAME;
        const isSession = !isClient && name.startsWith(SESSION_COOKIE_PREFIX);

        if (!isClient && !isSession) {
            continue;
        }

        // Only flag values that look like a GA cookie, so we ignore empty or
        // placeholder cookies (and unrelated _ga*-ish names).
        const looksLikeGa = isClient ? /^GA\d/.test(value) : /^GS\d/.test(value);

        if (!looksLikeGa || (isClient && UUID_CLIENT_VALUE.test(value))) {
            continue;
        }

        const parsed = isClient ? parseGaClientValue(value) : parseGaSessionValue(value);

        if (!parsed) {
            unparsable.push({ name, value });
        }
    }

    return unparsable;
};

// Track which cookies we've already reported this script load. Keyed by cookie
// NAME, not value: an unparsable format can carry a churning timestamp/counter,
// so keying by value would re-report on every 500ms poll and grow this set
// without bound. One report per cookie name per load is enough — the useful
// signal is the volume across many clients, not repeats from one.
const reportedUnparsableGaCookies = new Set<string>();

// Cap on the cookie value we ship to the backend, so a pathological cookie
// can't send a huge blob.
const MAX_REPORTED_COOKIE_VALUE_LENGTH = 200;

export function reportUnparsableGaCookies(cookies: readonly RawCookie[]): void {
    for (const { name, value } of findUnparsableGaCookies(cookies)) {
        if (reportedUnparsableGaCookies.has(name)) {
            continue;
        }

        reportedUnparsableGaCookies.add(name);

        const cookieValue = value.length > MAX_REPORTED_COOKIE_VALUE_LENGTH
            ? `${value.slice(0, MAX_REPORTED_COOKIE_VALUE_LENGTH)}...`
            : value;

        reportError('Unparseable GA cookie', { cookieName: name, cookieValue });
    }
}

function initClientTracking(): void {
    const client = parseGaClientCookie(readCookies(document.cookie));

    if (client) {
        window.dispatchEvent(new CustomEvent(
            '800dni.ga-client',
            {
                detail: {
                    clientId: client.clientId,
                    clientNumber: client.clientNumber,
                }
            }
        ));

        return;
    }

    setTimeout(initClientTracking, 500);
}

function initSessionTracking(foundSessions: string[]): void {
    const cookies = readCookies(document.cookie);

    // Covers both `_ga` and `_ga_<container>`; runs on every poll tick but
    // de-dupes internally, so it only reports each new bad cookie once.
    reportUnparsableGaCookies(cookies);

    const sessions = parseGaSessionCookies(cookies);

    const newFoundSessions: string[] = [];

    for (const session of sessions) {
        if (!foundSessions.includes(session.sessionName)) {
            newFoundSessions.push(session.sessionName);

            window.dispatchEvent(new CustomEvent(
                '800dni.ga-session',
                {
                    detail: {
                        sessionName: session.sessionName,
                        sessionId: session.sessionId,
                        sessionNumber: session.sessionNumber,
                    }
                }
            ));
        }
    }

    setTimeout(() => {
        initSessionTracking([...foundSessions, ...newFoundSessions]);
    }, 500);
}

export function initGoogleAnalytics(): void {
    initClientTracking();
    initSessionTracking([]);
}
